import React, {
  createContext, useCallback, useContext, useMemo, useState,
} from 'react';
import { PartnerSpecificFormData } from './PartnerSpecificFormConfig';

type Ctx = {
  partnerEnrollmentFormData: Record<string, PartnerSpecificFormData | null>;
  setEnrollmentFormData: (partnerName: string, data: PartnerSpecificFormData | null) => void;

  isPartnerEnrollmentDoneWithExtraStep: Record<string, boolean>;
  setEnrollmentExtraStepDoneState: (partnerName: string, done: boolean) => void;

  getPartnerEnrollmentFormData: (partnerName: string) => PartnerSpecificFormData | null;
  getPartnerEnrollmentDoneWithExtraStep: (partnerName: string) => boolean;
};

const PartnerEnrollmentContext = createContext<Ctx | null>(null);

export function usePartnerEnrollmentWithExtraStepContext() {
  const ctx = useContext(PartnerEnrollmentContext);
  if (!ctx) throw new Error('usePartnerEnrollment must be used within PartnerEnrollmentProvider');
  return ctx;
}

export const PartnerEnrollmentProvider: React.FC<{ children: React.ReactNode }> =
  ({ children }) => {
    const [partnerEnrollmentFormData, setPartnerEnrollmentFormData] =
      useState<Record<string, PartnerSpecificFormData | null>>({});
    const [isPartnerEnrollmentDoneWithExtraStep, setIsPartnerEnrollmentDoneWithExtraStep] =
      useState<Record<string, boolean>>({});

    const setEnrollmentFormData =
      useCallback((partnerName: string, data: PartnerSpecificFormData | null) => (
        setPartnerEnrollmentFormData((prev) => ({ ...prev, [partnerName]: data }))
      ), []);

    const setEnrollmentExtraStepDoneState =
      useCallback((partnerName: string, done: boolean) => {
        setIsPartnerEnrollmentDoneWithExtraStep((prev) => ({ ...prev, [partnerName]: done }));
      }, []);

    const getPartnerEnrollmentFormData = useCallback(
      (partnerName: string) => partnerEnrollmentFormData[partnerName] ?? null,
      [partnerEnrollmentFormData],
    );

    const getPartnerEnrollmentDoneWithExtraStep = useCallback(
      (partnerName: string) => !!isPartnerEnrollmentDoneWithExtraStep[partnerName],
      [isPartnerEnrollmentDoneWithExtraStep],
    );

    const value = useMemo<Ctx>(() => ({
      partnerEnrollmentFormData,
      setEnrollmentFormData,
      isPartnerEnrollmentDoneWithExtraStep,
      setEnrollmentExtraStepDoneState,
      getPartnerEnrollmentFormData,
      getPartnerEnrollmentDoneWithExtraStep,
    }), [
      partnerEnrollmentFormData,
      isPartnerEnrollmentDoneWithExtraStep,
      setEnrollmentFormData,
      setEnrollmentExtraStepDoneState,
      getPartnerEnrollmentFormData,
      getPartnerEnrollmentDoneWithExtraStep,
    ]);

    return (
      <PartnerEnrollmentContext.Provider value={value}>
        {children}
      </PartnerEnrollmentContext.Provider>
    );
  };
